import { BBCodeNode, ParseResult, ParseError, Token } from './bbcode.types';

export function parseBBCode(input: string): ParseResult {
  const errors: ParseError[] = [];

  try {
    const tokens = tokenize(input);
    const ast = buildAST(tokens, errors);
    return { ast, errors };
  } catch (error) {
    errors.push({
      message: error instanceof Error ? error.message : 'Unknown parsing error',
      position: 0,
    });
    return {
      ast: { type: 'document', children: [] },
      errors,
    };
  }
}

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  const tagPattern = /\[(\/?)([\w\d*]+)(?:\s+([^\]]+))?\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tagPattern.exec(input)) !== null) {
    if (match.index > lastIndex) {
      const textContent = input.slice(lastIndex, match.index);
      if (textContent) {
        tokens.push({
          type: 'text',
          content: textContent,
          position: lastIndex,
        });
      }
    }

    const isClosing = match[1] === '/';
    const tagName = match[2];
    const attributeString = match[3];

    if (isClosing) {
      tokens.push({
        type: 'closeTag',
        tag: tagName,
        position: match.index,
      });
    } else {
      const attributes = attributeString ? parseAttributes(attributeString) : {};
      tokens.push({
        type: 'openTag',
        tag: tagName,
        attributes,
        position: match.index,
      });
    }

    lastIndex = tagPattern.lastIndex;
  }

  if (lastIndex < input.length) {
    const textContent = input.slice(lastIndex);
    if (textContent) {
      tokens.push({
        type: 'text',
        content: textContent,
        position: lastIndex,
      });
    }
  }

  return tokens;
}

function parseAttributes(attributeString: string): Record<string, string> {
  const attributes: Record<string, string> = {};
  const attrPattern = /(\w+)=["']([^"']+)["']/g;
  let match: RegExpExecArray | null;

  while ((match = attrPattern.exec(attributeString)) !== null) {
    attributes[match[1]] = match[2];
  }

  return attributes;
}

function buildAST(tokens: Token[], errors: ParseError[]): BBCodeNode & { type: 'document' } {
  let position = 0;

  function parseNodes(endTags: string[] = []): BBCodeNode[] {
    const nodes: BBCodeNode[] = [];

    while (position < tokens.length) {
      const token = tokens[position];

      if (token.type === 'closeTag' && endTags.includes(token.tag || '')) {
        return nodes;
      }

      if (token.type === 'text') {
        const textContent = token.content || '';
        if (textContent.trim()) {
          const inlineNodes = parseInlineContent(textContent);
          nodes.push(...inlineNodes);
        }
        position++;
      } else if (token.type === 'openTag') {
        const node = parseTag(token, errors);
        if (node) {
          nodes.push(node);
        }
      } else if (token.type === 'closeTag') {
        errors.push({
          message: `Unexpected closing tag: ${token.tag}`,
          position: token.position,
          tag: token.tag,
        });
        position++;
      } else {
        position++;
      }
    }

    return nodes;
  }

  function parseTag(openToken: Token, errors: ParseError[]): BBCodeNode | null {
    const tagName = openToken.tag;
    position++;

    switch (tagName) {
      case 'p': {
        const align = openToken.attributes?.align as 'left' | 'center' | 'right' | undefined;
        const children = parseNodes(['p']);

        if (position < tokens.length && tokens[position].type === 'closeTag') {
          position++;
        } else {
          errors.push({
            message: 'Unclosed paragraph tag',
            position: openToken.position,
            tag: 'p',
          });
        }

        return {
          type: 'paragraph',
          align,
          children,
        };
      }

      case 'h2': {
        const align = openToken.attributes?.align as 'left' | 'center' | 'right' | undefined;
        const children = parseNodes(['h2']);

        if (position < tokens.length && tokens[position].type === 'closeTag') {
          position++;
        } else {
          errors.push({
            message: 'Unclosed heading tag',
            position: openToken.position,
            tag: 'h2',
          });
        }

        return {
          type: 'heading',
          level: 2,
          align,
          children,
        };
      }

      case 'b': {
        const children = parseNodes(['b']);

        if (position < tokens.length && tokens[position].type === 'closeTag') {
          position++;
        }

        return {
          type: 'bold',
          children,
        };
      }

      case 'i': {
        const children = parseNodes(['i']);

        if (position < tokens.length && tokens[position].type === 'closeTag') {
          position++;
        }

        return {
          type: 'italic',
          children,
        };
      }

      case 'img': {
        const src = openToken.attributes?.src || '';

        if (position < tokens.length && tokens[position].type === 'closeTag') {
          position++;
        }

        return {
          type: 'image',
          src,
        };
      }

      case 'list': {
        const children = parseListItems(errors);

        if (position < tokens.length && tokens[position].type === 'closeTag') {
          position++;
        } else {
          errors.push({
            message: 'Unclosed list tag',
            position: openToken.position,
            tag: 'list',
          });
        }

        return {
          type: 'list',
          children,
        };
      }

      default:
        errors.push({
          message: `Unknown tag: ${tagName}`,
          position: openToken.position,
          tag: tagName,
        });
        return null;
    }
  }

  function parseListItems(errors: ParseError[]): Array<BBCodeNode & { type: 'listItem' }> {
    const items: Array<BBCodeNode & { type: 'listItem' }> = [];

    while (position < tokens.length) {
      const token = tokens[position];

      if (token.type === 'closeTag' && token.tag === 'list') {
        break;
      }

      if (token.type === 'openTag' && token.tag === '*') {
        position++;
        const children = parseNodes(['*']);

        if (position < tokens.length && tokens[position].type === 'closeTag') {
          position++;
        }

        items.push({
          type: 'listItem',
          children,
        });
      } else {
        position++;
      }
    }

    return items;
  }

  const children = parseNodes();

  return {
    type: 'document',
    children,
  };
}

function parseInlineContent(content: string): BBCodeNode[] {
  const inlinePattern = /\[(\/?)(b|i)\]/g;
  const nodes: BBCodeNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const tagStack: Array<{ tag: string; startIndex: number; children: BBCodeNode[] }> = [];

  while ((match = inlinePattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textContent = content.slice(lastIndex, match.index);
      const textNode: BBCodeNode = {
        type: 'text',
        content: textContent,
      };

      if (tagStack.length > 0) {
        tagStack[tagStack.length - 1].children.push(textNode);
      } else {
        nodes.push(textNode);
      }
    }

    const isClosing = match[1] === '/';
    const tagName = match[2];

    if (isClosing) {
      if (tagStack.length > 0 && tagStack[tagStack.length - 1].tag === tagName) {
        const completedTag = tagStack.pop();
        if (completedTag) {
          const node: BBCodeNode =
            tagName === 'b'
              ? { type: 'bold', children: completedTag.children }
              : { type: 'italic', children: completedTag.children };

          if (tagStack.length > 0) {
            tagStack[tagStack.length - 1].children.push(node);
          } else {
            nodes.push(node);
          }
        }
      }
    } else {
      tagStack.push({ tag: tagName, startIndex: match.index, children: [] });
    }

    lastIndex = inlinePattern.lastIndex;
  }

  if (lastIndex < content.length) {
    const textContent = content.slice(lastIndex);
    const textNode: BBCodeNode = {
      type: 'text',
      content: textContent,
    };

    if (tagStack.length > 0) {
      tagStack[tagStack.length - 1].children.push(textNode);
    } else {
      nodes.push(textNode);
    }
  }

  while (tagStack.length > 0) {
    const incompletedTag = tagStack.pop();
    if (incompletedTag) {
      nodes.push({
        type: 'text',
        content: `[${incompletedTag.tag}]`,
      });
      nodes.push(...incompletedTag.children);
    }
  }

  return nodes;
}
